import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageCircle, Send, Bot, User, Loader2, Trash2, Sparkles, 
  ImagePlus, X, Plus, Edit2, Check, ChevronLeft, MoreVertical, Menu
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TexChatFloating = ({ 
  isOpen, 
  onClose, 
  contextType = null, 
  contextData = null,
  contextImage = null,
  onClearContext = null
}) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [attachedImage, setAttachedImage] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [showMenu, setShowMenu] = useState(null);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Set context image when provided from other tools
  useEffect(() => {
    if (contextImage && isOpen) {
      setAttachedImage({
        base64: contextImage,
        preview: contextImage.startsWith('data:') ? contextImage : `data:image/jpeg;base64,${contextImage}`
      });
    }
  }, [contextImage, isOpen]);

  // Fetch conversations on open
  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API_URL}/api/texvision/texchat/conversations`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Error cargando conversaciones'));
          }
        };
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.send();
      });
      setConversations(res.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  // Load conversation messages
  const loadConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API_URL}/api/texvision/texchat/conversations/${conversationId}`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Error cargando conversación'));
          }
        };
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.send();
      });
      setActiveConversation(res);
      setMessages(res.messages || []);
    } catch (error) {
      toast.error('Error cargando conversación');
    }
  };

  // Create new conversation
  const createNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setInput('');
    setAttachedImage(null);
    inputRef.current?.focus();
  };

  // Rename conversation
  const renameConversation = async (conversationId, newTitle) => {
    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${API_URL}/api/texvision/texchat/conversations/${conversationId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Error renombrando'));
          }
        };
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.send(JSON.stringify({ title: newTitle }));
      });
      
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId ? { ...c, title: newTitle } : c
      ));
      if (activeConversation?.conversation_id === conversationId) {
        setActiveConversation(prev => ({ ...prev, title: newTitle }));
      }
      setEditingTitle(null);
      toast.success('Conversación renombrada');
    } catch (error) {
      toast.error('Error al renombrar');
    }
  };

  // Delete conversation
  const deleteConversation = async (conversationId) => {
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    
    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${API_URL}/api/texvision/texchat/conversations/${conversationId}`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error('Error eliminando'));
          }
        };
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.send();
      });
      
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      if (activeConversation?.conversation_id === conversationId) {
        createNewConversation();
      }
      toast.success('Conversación eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
    setShowMenu(null);
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      const base64Data = base64.split(',')[1];
      setAttachedImage({
        base64: base64Data,
        preview: base64
      });
    };
    reader.readAsDataURL(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachedImage = () => {
    setAttachedImage(null);
    if (onClearContext) onClearContext();
  };

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    const imageToSend = attachedImage;
    setInput('');
    setAttachedImage(null);
    if (onClearContext) onClearContext();
    
    const userMsgObj = { 
      role: 'user', 
      content: userMessage,
      has_image: !!imageToSend,
      context_type: contextType
    };
    const newMessages = [...messages, userMsgObj];
    setMessages(newMessages);
    setLoading(true);

    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      
      const requestBody = {
        message: userMessage,
        conversation_id: activeConversation?.conversation_id || null
      };
      
      if (imageToSend?.base64) {
        requestBody.image_base64 = imageToSend.base64;
      }
      
      if (contextType && contextData) {
        requestBody.context_type = contextType;
        requestBody.context_data = contextData;
      }
      
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/texvision/texchat`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Error en el chat'));
            }
          } catch (e) {
            reject(new Error('Error procesando respuesta'));
          }
        };
        
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.timeout = 90000;
        xhr.ontimeout = () => reject(new Error('Timeout - intenta de nuevo'));
        
        xhr.send(JSON.stringify(requestBody));
      });

      // Update conversation ID if new
      if (!activeConversation && data.conversation_id) {
        setActiveConversation({ 
          conversation_id: data.conversation_id,
          title: userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : '')
        });
        fetchConversations(); // Refresh list
      }

      setMessages([...newMessages, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error.message || 'Error al enviar mensaje');
      setMessages(messages);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  const chatPanel = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white sm:rounded-2xl shadow-2xl w-full h-full sm:h-[80vh] sm:max-h-[700px] sm:max-w-4xl flex overflow-hidden pb-16 sm:pb-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sidebar - Conversations List - Hidden on mobile by default */}
        <div className={`${showMobileSidebar ? 'flex' : 'hidden'} sm:flex w-full sm:w-72 bg-slate-50 border-r border-slate-200 flex-col absolute sm:relative inset-0 sm:inset-auto z-10`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-2">
            <Button 
              onClick={() => {
                createNewConversation();
                setShowMobileSidebar(false);
              }}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva conversación
            </Button>
            {/* Close sidebar on mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMobileSidebar(false)}
              className="sm:hidden"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loadingConversations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 px-4">
                <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No hay conversaciones</p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {conversations.map((conv) => (
                  <div 
                    key={conv.conversation_id}
                    className={`group relative rounded-lg transition-colors ${
                      activeConversation?.conversation_id === conv.conversation_id 
                        ? 'bg-amber-100' 
                        : 'hover:bg-slate-100'
                    }`}
                  >
                    {editingTitle === conv.conversation_id ? (
                      <div className="flex items-center gap-1 p-2">
                        <input
                          type="text"
                          value={editTitleValue}
                          onChange={(e) => setEditTitleValue(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border rounded"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              renameConversation(conv.conversation_id, editTitleValue);
                            } else if (e.key === 'Escape') {
                              setEditingTitle(null);
                            }
                          }}
                        />
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="w-7 h-7"
                          onClick={() => renameConversation(conv.conversation_id, editTitleValue)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          loadConversation(conv.conversation_id);
                          setShowMobileSidebar(false);
                        }}
                        className="w-full text-left p-3 pr-10"
                      >
                        <p className="text-sm font-medium text-slate-700 truncate">
                          {conv.title}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(conv.updated_at).toLocaleDateString('es-MX', { 
                            day: 'numeric', 
                            month: 'short' 
                          })}
                        </p>
                      </button>
                    )}
                    
                    {/* Menu button */}
                    {editingTitle !== conv.conversation_id && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-7 h-7 opacity-0 group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(showMenu === conv.conversation_id ? null : conv.conversation_id);
                          }}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                        
                        {showMenu === conv.conversation_id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                            <button
                              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditTitleValue(conv.title);
                                setEditingTitle(conv.conversation_id);
                                setShowMenu(null);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                              Renombrar
                            </button>
                            <button
                              className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteConversation(conv.conversation_id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                              Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {/* Menu button for mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowMobileSidebar(true)}
                className="sm:hidden flex-shrink-0"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">
                  {activeConversation?.title || 'TexChat AI'}
                </h3>
                <p className="text-xs text-slate-500 hidden sm:block">Experto en industria textil</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-2 sm:px-4 overflow-y-auto">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
                </div>
                <h4 className="text-base sm:text-lg font-medium text-slate-900 mb-2">
                  {contextType ? 'Pregunta sobre tu resultado' : '¡Hola! Soy TexChat AI'}
                </h4>
                <p className="text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4 max-w-md">
                  {contextType === 'fashion_design' 
                    ? 'Pregúntame sobre el diseño que acabas de crear.'
                    : contextType === 'visual_search'
                    ? 'Puedo darte más detalles sobre la imagen analizada.'
                    : contextType === 'textrend'
                    ? 'Profundicemos en las tendencias.'
                    : 'Pregúntame sobre telas, confección, materiales y más.'
                  }
                </p>
                
                {/* Context indicator */}
                {contextType && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 sm:mb-4">
                    <p className="text-xs sm:text-sm text-amber-700">
                      {contextType === 'fashion_design' && '📐 Desde: Diseñador'}
                      {contextType === 'visual_search' && '🔍 Desde: Buscador Visual'}
                      {contextType === 'textrend' && '📊 Desde: TexTrend'}
                    </p>
                  </div>
                )}

                {/* Suggested questions based on context */}
                <div className="w-full max-w-md space-y-2">
                  <p className="text-xs text-slate-400 mb-2">Preguntas sugeridas:</p>
                  {(contextType === 'fashion_design' ? [
                    "¿Qué tela me recomiendas?",
                    "¿Cuánto consumiría de tela?",
                    "¿Proceso de confección?"
                  ] : contextType === 'visual_search' ? [
                    "¿Dónde consigo esta tela en MX?",
                    "¿Alternativas más económicas?",
                    "¿Costo por metro?"
                  ] : contextType === 'textrend' ? [
                    "¿Cómo aplico esta tendencia?",
                    "¿Qué materiales usar?",
                    "¿Público objetivo?"
                  ] : [
                    "¿Tela para sudaderas?",
                    "¿French terry vs fleece?",
                    "¿Cómo calculo consumo?",
                    "¿Proveedores en México?",
                    "¿Qué aguja uso para lycra?"
                  ]).map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInput(question.replace('📷 ', ''));
                        inputRef.current?.focus();
                      }}
                      className="w-full text-left px-3 py-2 text-xs sm:text-sm bg-slate-50 hover:bg-amber-50 rounded-lg border border-slate-200 hover:border-amber-200 transition-colors text-slate-600 hover:text-slate-900"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] px-4 py-3 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-amber-500 text-white rounded-br-md'
                          : 'bg-slate-100 text-slate-900 rounded-bl-md'
                      }`}
                    >
                      {msg.has_image && (
                        <div className="mb-2 text-xs opacity-70">
                          [Imagen adjunta]
                        </div>
                      )}
                      {msg.context_type && (
                        <div className="mb-2 text-xs opacity-70">
                          [{msg.context_type === 'fashion_design' ? 'Desde Diseñador' : 
                            msg.context_type === 'visual_search' ? 'Desde Buscador Visual' : 
                            msg.context_type === 'textrend' ? 'Desde TexTrend' : ''}]
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                ))}
                
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                        <span className="text-sm text-slate-500">Pensando...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t bg-slate-50 p-3 sm:p-4">
            {/* Image preview */}
            {attachedImage && (
              <div className="mb-2 sm:mb-3 flex items-start gap-2">
                <div className="relative">
                  <img 
                    src={attachedImage.preview} 
                    alt="Preview" 
                    className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <button
                    onClick={removeAttachedImage}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <span className="text-xs text-slate-500 mt-1">
                  {contextType ? 'Del análisis' : 'Adjunta'}
                </span>
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="flex-shrink-0 border-slate-200 hover:bg-amber-50 hover:border-amber-300 w-10 h-10"
                title="Adjuntar imagen"
              >
                <ImagePlus className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
              </Button>
              
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={attachedImage ? "Pregunta..." : "Tu pregunta..."}
                className="flex-1 px-3 sm:px-4 py-2 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm min-w-0"
                rows={1}
                disabled={loading}
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 sm:px-4 flex-shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1 sm:mt-2 text-center hidden">
              1 crédito/mensaje
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(chatPanel, document.body);
};

export default TexChatFloating;
